type PerformanceMetadata = Record<string, unknown>;

type PerformanceEntry = {
  label: string;
  durationMs: number;
  startedAt: number;
  endedAt: number;
  metadata?: PerformanceMetadata;
};

type ActiveEntry = {
  startedAt: number;
  metadata?: PerformanceMetadata;
};

type PerformanceStore = {
  active: Map<string, ActiveEntry>;
  completed: PerformanceEntry[];
};

declare global {
  interface Window {
    __gastronomiaPerformance?: PerformanceStore;
  }
}

const MAX_COMPLETED_ENTRIES = 300;

function isEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return import.meta.env.DEV || window.localStorage.getItem('debug_performance') === '1';
  } catch {
    return import.meta.env.DEV;
  }
}

function getStore(): PerformanceStore {
  if (typeof window === 'undefined') {
    return {
      active: new Map<string, ActiveEntry>(),
      completed: [],
    };
  }

  if (!window.__gastronomiaPerformance) {
    window.__gastronomiaPerformance = {
      active: new Map<string, ActiveEntry>(),
      completed: [],
    };
  }

  return window.__gastronomiaPerformance;
}

function mergeMetadata(base?: PerformanceMetadata, extra?: PerformanceMetadata): PerformanceMetadata | undefined {
  if (!base && !extra) {
    return undefined;
  }

  return {
    ...(base ?? {}),
    ...(extra ?? {}),
  };
}

function pushCompletedEntry(entry: PerformanceEntry): void {
  const store = getStore();
  store.completed.push(entry);

  if (store.completed.length > MAX_COMPLETED_ENTRIES) {
    store.completed.splice(0, store.completed.length - MAX_COMPLETED_ENTRIES);
  }
}

export function startPerformanceTrace(label: string, metadata?: PerformanceMetadata): void {
  if (!isEnabled()) {
    return;
  }

  getStore().active.set(label, {
    startedAt: performance.now(),
    metadata,
  });
}

export function endPerformanceTrace(label: string, metadata?: PerformanceMetadata): number | null {
  if (!isEnabled()) {
    return null;
  }

  const store = getStore();
  const activeEntry = store.active.get(label);

  if (!activeEntry) {
    return null;
  }

  store.active.delete(label);

  const endedAt = performance.now();
  const durationMs = endedAt - activeEntry.startedAt;
  const mergedMetadata = mergeMetadata(activeEntry.metadata, metadata);

  pushCompletedEntry({
    label,
    durationMs,
    startedAt: activeEntry.startedAt,
    endedAt,
    metadata: mergedMetadata,
  });

  console.info(`[Perf] ${label}: ${durationMs.toFixed(1)}ms`, mergedMetadata ?? {});

  return durationMs;
}

export async function measurePerformance<T>(label: string, task: () => Promise<T>, metadata?: PerformanceMetadata): Promise<T> {
  startPerformanceTrace(label, metadata);

  try {
    return await task();
  } finally {
    endPerformanceTrace(label);
  }
}

export function recordPerformance(label: string, durationMs: number, metadata?: PerformanceMetadata): void {
  if (!isEnabled()) {
    return;
  }

  const endedAt = performance.now();
  pushCompletedEntry({
    label,
    durationMs,
    startedAt: endedAt - durationMs,
    endedAt,
    metadata,
  });

  console.info(`[Perf] ${label}: ${durationMs.toFixed(1)}ms`, metadata ?? {});
}

export function getPerformanceEntries(prefix?: string): PerformanceEntry[] {
  const entries = getStore().completed.slice();

  const filteredEntries = prefix ? entries.filter((entry) => entry.label.startsWith(prefix)) : entries;

  return filteredEntries.sort((left, right) => right.durationMs - left.durationMs);
}

export function printPerformanceSummary(prefix?: string, limit = 10): PerformanceEntry[] {
  if (!isEnabled()) {
    return [];
  }

  const entries = getPerformanceEntries(prefix).slice(0, limit);

  if (entries.length === 0) {
    console.info(`[Perf] No entries recorded${prefix ? ` for ${prefix}` : ''}`);
    return entries;
  }

  console.table(
    entries.map((entry) => ({
      label: entry.label,
      durationMs: Number(entry.durationMs.toFixed(1)),
      ...entry.metadata,
    })),
  );

  return entries;
}

export function clearPerformanceEntries(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.__gastronomiaPerformance = {
    active: new Map<string, ActiveEntry>(),
    completed: [],
  };
}